<script lang="ts">

  import { page } from "$app/state"
  import httpClient from "$lib/axios-client"
  import { Button } from "$lib/components/ui/button"
  import { CalendarMonth } from "$lib/components/ui/calendar-month"
  import { Card, CardContent, CardHeader, CardTitle } from "$lib/components/ui/card"
  import { Popover, PopoverContent, PopoverTrigger } from "$lib/components/ui/popover"
  import type { _response } from "$lib/data/type/_response"
  import { useDialog } from "$lib/hooks/use-dialog.svelte"
  import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date"
  import ArrowLeftIcon from "@lucide/svelte/icons/arrow-left"
  import CalendarIcon from "@lucide/svelte/icons/calendar"
  import LoaderCircleIcon from "@lucide/svelte/icons/loader-circle"
  import RefreshCwIcon from "@lucide/svelte/icons/refresh-cw"
  import { createInfiniteQuery, createQuery } from "@tanstack/svelte-query"
  import DialogDetail from "./_components/dialog-detail.svelte"
  import type { Order } from "./_types"

  type Dash = {
    count: number
    total_outstanding: number
  }

  const prefix = $derived(page.params.prefix ?? "0")

  const initialDate = today(getLocalTimeZone())

  let bottomOfPage = $state<HTMLDivElement>()

  let month = $state(new CalendarDate(initialDate.year, initialDate.month, 1))

  let select = $state("")
  const detailDialog = useDialog()

  const date = $derived(month.toString())
  const monthLabel = $derived(month.format("MMMM yyyy"))

  const dash = createQuery(() => ({
    queryKey: ["order-outstanding-dash", {
      prefix,
      date
    }],
    queryFn: async () => {
      const response = await httpClient.get<_response<Dash>>(`/api/${prefix}/order/outstandings/dash`, {
        params: {
          date
        }
      })

      return response.data.result!
    }
  }))

  const query = createInfiniteQuery(() => ({
    queryKey: ["order-outstanding", {
      prefix,
      date
    }],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {

      const response = await httpClient.get<_response<Order[]>>(`/api/${prefix}/order/outstandings`, {
        params: {
          page: pageParam,
          date
        }
      })

      return response.data.result!
    },
    getNextPageParam: (last, pages) => (last.length === 0 ? undefined : pages.length),
  }))


  const rows = $derived(query.data?.pages.flat() ?? [])

  function handleRefresh() {
    void dash.refetch()
    void query.refetch()
  }

  $effect(() => {
    if (!bottomOfPage || !query.hasNextPage || query.isFetching) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) void query.fetchNextPage()
      },
      { threshold: 0.5 },
    )

    observer.observe(bottomOfPage)
    return () => observer.disconnect()
  })

</script>

<main class="overflow-auto relative mx-auto h-full max-w-3xl">
  <nav class="sticky w-full top-0 inset-s-0 inset-e-0 z-10 bg-background border-b max-w-3xl">
    <div class="flex h-12 items-center justify-between px-3">
      <div class="flex items-center">
        <Button
          href={`/${prefix}/home`}
          variant="ghost"
          size="icon"
          class="me-2 rounded-full">
          <ArrowLeftIcon aria-hidden="true"/>
        </Button>
        <h1 class="text-lg font-medium">Outstanding</h1>
      </div>
      <div></div>
    </div>
    <div class="flex gap-2 px-3 py-2">
      <Button
        variant="outline"
        size="icon"
        class="group"
        data-loading={query.isFetching}
        disabled={query.isFetching}
        onclick={handleRefresh}>
        <LoaderCircleIcon
          class="invisible absolute animate-spin group-data-[loading=true]:visible"
          aria-hidden="true"/>
        <RefreshCwIcon
          class="group-data-[loading=true]:invisible"
          aria-hidden="true"/>
      </Button>
      <Popover>
        <PopoverTrigger>
          {#snippet child({ props })}
            <Button
              {...props}
              variant="outline"
              class="justify-start text-left font-normal">
              <CalendarIcon aria-hidden="true"/>
              {monthLabel}
            </Button>
          {/snippet}
        </PopoverTrigger>
        <PopoverContent
          align="start"
          class="w-auto p-0">
          <CalendarMonth
            selected={{ year: month.year, month: month.month }}
            onSelect={(value) => {
              month = value
              document.keyEsc()
            }}/>
        </PopoverContent>
      </Popover>
    </div>
  </nav>
  <div class="min-h-full">
    <div class="h-3"></div>
    <div class="mx-3 grid grid-cols-2 gap-3">
      <Card
        size="sm"
        class="gap-0 py-0 shadow-none">
        <CardHeader class="px-2 pt-2 pb-0">
          <CardTitle class="text-xs font-normal leading-none">Order</CardTitle>
        </CardHeader>
        <CardContent class="px-2 pb-2 text-xl font-semibold tabular-nums tracking-tight">
          {dash.data?.count ?? 0}
        </CardContent>
      </Card>
      <Card
        size="sm"
        class="gap-0 py-0 shadow-none">
        <CardHeader class="px-2 pt-2 pb-0">
          <CardTitle class="text-xs font-normal leading-none">Outstanding</CardTitle>
        </CardHeader>
        <CardContent class="px-2 pb-2 text-xl font-semibold tabular-nums tracking-tight">
          Rp{(dash.data?.total_outstanding ?? 0).toLocaleString("en-US")}
        </CardContent>
      </Card>
    </div>
    <div class="h-3"></div>
    {#if !query.isFetching && !query.isError && rows.isEmpty()}
      <div class="px-3 py-4 text-center text-xs">Tidak ada data</div>
    {:else if query.isError}
      <div class="px-3 py-4 text-center text-xs">Gagal memuat data</div>
    {/if}
    {#if !query.isError}
      {#each rows as order (order.id)}
        <Button
          variant="ghost"
          class="block h-auto w-full rounded-none px-3 py-2 text-left text-sm hover:bg-muted"
          onclick={() => {
            select = order.id.toString()
            detailDialog.setOpen(true)
          }}>
          <div class="flex w-full items-center gap-1 text-xs text-muted-foreground">
            <div class="tabular-nums tracking-tight">{order.date.toDateFormat("dd/MM/yyyy")}</div>
            <div class="text-[0.5rem]">|</div>
            <div class="tabular-nums tracking-tight">{order.number}</div>
            <div class="flex-1"></div>
            <div class="tabular-nums tracking-tight">{(order.amount ?? 0).toLocaleString("en-US")}</div>
          </div>
          <div class="w-full">{order.customer.name}</div>
        </Button>
      {/each}
    {/if}
  </div>
  <div
    bind:this={bottomOfPage}
    class="h-1">
  </div>
</main>
<DialogDetail
  open={detailDialog.state}
  setOpen={detailDialog.setOpen}
  select={select}/>
